import { HierarchicalFacetNode } from '../types';
import { useState } from 'preact/hooks';

interface HierarchicalFacetTreeProps {
  nodes: HierarchicalFacetNode[];
  selectedPaths: string[][];
  onFacetClick: (path: string[]) => void;
  onToggleExpansion: (path: string[]) => void;
}

interface FacetNodeProps {
  node: HierarchicalFacetNode;
  selectedPaths: string[][];
  onFacetClick: (path: string[]) => void;
  onToggleExpansion: (path: string[]) => void;
}

function FacetNode({
  node,
  selectedPaths,
  onFacetClick,
  onToggleExpansion,
}: FacetNodeProps) {
  const [showAllChildren, setShowAllChildren] = useState(false);
  const INITIAL_VISIBLE_COUNT = 5;

  const isSelected = selectedPaths.some(
    (selectedPath) =>
      selectedPath.length === node.path.length &&
      selectedPath.every((segment, index) => segment === node.path[index])
  );

  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = node.isExpanded;

  // Show expand button only when the node actually has children
  const showExpandButton = node.has_children;

  // Check if any descendant is selected (when collapsed)
  const hasSelectedDescendant =
    !isExpanded &&
    selectedPaths.some(
      (selectedPath) =>
        selectedPath.length > node.path.length &&
        selectedPath
          .slice(0, node.path.length)
          .every((segment, index) => segment === node.path[index])
    );

  const handleToggleClick = (e: Event) => {
    e.stopPropagation();
    onToggleExpansion(node.path);
  };

  const handleFacetClick = () => {
    onFacetClick(node.path);
  };

  const visibleChildren = showAllChildren
    ? node.children
    : node.children?.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMoreChildren =
    hasChildren && node.children!.length > INITIAL_VISIBLE_COUNT;

  return (
    <div className={`facet-node level-${node.level}`}>
      <div
        className={`facet-node-content ${isSelected ? 'active' : ''} ${hasSelectedDescendant ? 'has-selected-child' : ''}`}
      >
        {showExpandButton && (
          <button
            className={`facet-expand-button ${isExpanded ? 'expanded' : 'collapsed'} ${hasSelectedDescendant ? 'has-selected-child' : ''}`}
            onClick={handleToggleClick}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            title={`Click to ${isExpanded ? 'collapse' : 'expand'}${hasSelectedDescendant ? ' (has selected child)' : ''}`}
          >
            {isExpanded ? '-' : '+'}
          </button>
        )}
        {!showExpandButton && <span className="facet-indent"> </span>}

        <button
          className="facet-toggle"
          onClick={handleFacetClick}
          title={`${node.displayValue} (${node.count} results)`}
        >
          <span className="facet-value">
            {node.displayValue}
            {hasSelectedDescendant && (
              <span className="selected-child-indicator">●</span>
            )}
          </span>
          <span className="facet-count">{node.count}</span>
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            style={{ marginLeft: 4 }}
          />
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div className="facet-children">
          {visibleChildren!.map((child) => (
            <FacetNode
              key={child.path.join('/')}
              node={child}
              selectedPaths={selectedPaths}
              onFacetClick={onFacetClick}
              onToggleExpansion={onToggleExpansion}
            />
          ))}
          {hasMoreChildren && !showAllChildren && (
            <button
              className="facet-expand-all-button"
              onClick={() => setShowAllChildren(true)}
            >
              Mostra di più +
            </button>
          )}
          {hasMoreChildren && showAllChildren && (
            <button
              className="facet-expand-all-button"
              onClick={() => setShowAllChildren(false)}
            >
              Mostra di meno -
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function HierarchicalFacetTree({
  nodes,
  selectedPaths,
  onFacetClick,
  onToggleExpansion,
}: HierarchicalFacetTreeProps) {
  const [showAll, setShowAll] = useState(false);
  const INITIAL_VISIBLE_COUNT = 5;

  const visibleNodes = showAll ? nodes : nodes.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore = nodes.length > INITIAL_VISIBLE_COUNT;

  return (
    <div className="hierarchical-facet-tree">
      {visibleNodes.map((node) => (
        <FacetNode
          key={node.path.join('/')}
          node={node}
          selectedPaths={selectedPaths}
          onFacetClick={onFacetClick}
          onToggleExpansion={onToggleExpansion}
        />
      ))}
      {hasMore && !showAll && (
        <button
          className="facet-expand-all-button"
          onClick={() => setShowAll(true)}
        >
          Mostra di più +
        </button>
      )}
      {hasMore && showAll && (
        <button
          className="facet-expand-all-button"
          onClick={() => setShowAll(false)}
        >
          Mostra di meno -
        </button>
      )}
    </div>
  );
}
